import {AssertWarn, Clone, GetValues_ForSchema, Timer, WaitXThenRun} from "js-vextensions";
import {AddSchema, CreateAccessor} from "mobx-graphlink";
import {minuteInMS, RunInAction, weekInMS} from "web-vcore";
import {GetEntities_WithUserTag} from "../../../Server/DB/entities.js";
import {EngineConfig_Alarms} from "../../../Server/DB/engineConfigs/@EngineConfig/@EC_Alarms.js";
import {AlarmSequence} from "../../../Server/DB/engineConfigs/@EngineConfig/Alarms/@AlarmConfig.js";
import {GetJournalEntries, Journey_GetSessionPeriodsToShow} from "../../../Server/DB/journalEntries.js";
import {JournalSegment} from "../../../Server/DB/journalEntries/@JournalEntry.js";
import {GetSessions} from "../../../Server/DB/sessions.js";
import {MeID} from "../../../Server/DB/users.js";
import {store} from "../../../Store/index.js";
import {StatsGrouping, StatsXType, StatsXType_tickStepInfos, StatsYType} from "../../../Server/DB/statViews/@StatTypes.js";
import {LogType} from "../../../UI/Tools/@Shared/LogEntry.js";
import {LightPlayer} from "../../../Utils/EffectPlayers/LightPlayer.js";
import {SoundPlayer} from "../../../Utils/EffectPlayers/SoundPlayer.js";
import {NarrateText_ForEngineComp} from "../../../Utils/Services/TTS.js";
import {LiveSession, TriggerPackage} from "../../LiveSession.js";
import {SessionEvent} from "../SessionEvent.js";
import {AlarmComp} from "./AlarmComps/@AlarmComp.js";
import {EngineSessionComp} from "./EngineSessionComp.js";
import {ModeSwitcherComp} from "./ModeSwitcherComp.js";
import {GetAudioFilesWithSubpath} from "../../../Store/main/general/CacheState.js";
import {UpdateJournalEntry} from "../../../Server/Commands/JournalEntryCommands.js";
import {SessionCoreData} from "../../../Server/DB/sessions/@Session.js";
import {PlaySound} from "../../../Utils/Bridge/Bridge_Native/MediaPlayer.js";
import {CreateAggregationMeta, JStatsUI_GetAggregationData} from "../../../UI/Stats/AggregationData.js";
import {JStatsUI_GetInitialTicks} from "../../../UI/Stats/Helpers.js";
import React from "react";
import {Row} from "react-vcomponents";
import {Phase} from "../../../Server/DB/@Shared/Phase.js";

export class PhaseChange_ExtraMessage {
	constructor(data?: Partial<PhaseChange_ExtraMessage>) { Object.assign(this, data); }
	toPhaseDrifting_requestedDurationReduction: number|n; // in ms
	toPhaseSolving_requestedPhaseOnSolveFail: Phase|n;
}

/** Returned alarm-delays are in minutes. */
export const GetAlarmDelayCandidates = CreateAccessor((config: EngineConfig_Alarms)=>{
	return config.alarmDelay_pool ?? [];
});
export const GetNextAlarmDelayTickets = CreateAccessor((alarmDelayCandidates: number[], alarmDelayRates: {[key: string]: number}, catchUpRate: number)=>{
	const {view} = store.main.stats;
	const xTicks_initial = JStatsUI_GetInitialTicks(view);
	const sessions = GetSessions(MeID());
	const dreams = GetJournalEntries(MeID());
	const meta = CreateAggregationMeta(
		"cycleInNight", "dreamSegments_sum", [], StatsGrouping.alarmDelay, "", view.maxMemoryPromptRecallGap, null,
		view.dateRange_enabled ?? false, view.dateRange_min, view.dateRange_max, // use these props, since stat-view may align with user's criteria
		null,
	);
	const aggregationData = JStatsUI_GetAggregationData(sessions, dreams, [], meta, xTicks_initial, []); // note: unlike stat-view, no group-renames here; selection here always based on recorded group-names

	// 1) Calculate the number of first-cycle dream-segments that exist for each alarm-delay.
	// 2) For each alarm-delay, calculate the "target segment-count" for that delay (with "rate" applied), based on the segment-counts of the other delays.
	// 3) Find distance to that target (keep at least 1, since no alarm-delay should have 0% chance), then add that many "tickets" to the "ticket pool" for this delay.
	// 4) We randomly select one of those "tickets" from the pool as our current alarm-delay. (helping to settle on equivalent counts)

	const segmentCountsPerAlarmDelay = alarmDelayCandidates.ToMap(alarmDelay=>alarmDelay, alarmDelay=>{
		const alarmDelayAsMinutesStr = alarmDelay.toString();
		return aggregationData.segmentExistenceSamples.GetSampleValues(1, alarmDelayAsMinutesStr).Count();
	});

	const alarmDelayTickets = [] as number[];
	for (const alarmDelay of alarmDelayCandidates) {
		if (alarmDelayRates[alarmDelay] == 0) continue; // if rate is 0, completely ignore this option
		const adjustedSegmentCount_leader = alarmDelayCandidates.filter(a=>a != alarmDelay && a != 0).map(otherDelay=>{
			const ourRateDividedByOtherRate = (alarmDelayRates[alarmDelay] ?? 1) / (alarmDelayRates[otherDelay] ?? 1);
			return segmentCountsPerAlarmDelay.get(otherDelay)! * ourRateDividedByOtherRate;
		}).concat(0).Max();

		const segmentCount = segmentCountsPerAlarmDelay.get(alarmDelay)!;
		const distFromLeader = adjustedSegmentCount_leader - segmentCount;
		const ticketCount = distFromLeader.KeepBetween(1, catchUpRate);
		for (let i = 0; i < ticketCount; i++) {
			alarmDelayTickets.push(alarmDelay);
		}
	}

	return alarmDelayTickets;
});


export class AlarmsComp extends EngineSessionComp<EngineConfig_Alarms> {
	constructor(session: LiveSession, config: EngineConfig_Alarms) {
		super(session, config, s=>config.enabled, s=>s.IsLocal());
	}

	override GetStatusUI() {
		/*return <>
			<Row>Mode: {this.mode} (as of: {moment(this.mode_changedAt).format("HH:mm:ss")})</Row>
		</>;*/
		return React.createElement(
			React.Fragment,
			null,
			React.createElement(Row, null, `Phase: ${this.phase} (as of: ${new Date(this.phase_changedAt).toLocaleTimeString()})`),
		);
	}

	override GetTriggerPackages() {
		return [
			new TriggerPackage("Alarms_SignalAwakeness", this.c.signalAwakeness_triggerSet, this, {}, async triggerInfo=>{
				this.SignalAwakeness();
			}),
		];
	}

	async NarrateText(text: string, volumeMultiplier = 1) {
		await NarrateText_ForEngineComp(this, text, this.c.volumeMultiplier * volumeMultiplier, this.c.voiceSoundTag);
	}

	// players
	phaseSleep_soundPlayer = new SoundPlayer();
	phaseSleep_lightPlayer = new LightPlayer();
	phaseSolving_soundPlayer = new SoundPlayer();
	phaseSolving_lightPlayer = new LightPlayer();
	//StopPlayers_SleepOrSolving() {}
	/*onPhaseEnter_soundPlayer = new SoundPlayer();
	onPhaseEnter_lightPlayer = new LightPlayer();*/

	// in-session timers
	sleepUntilAlarmTimer!: Timer;
	requiredSolveProgressTimer!: Timer;
	giveUpOnSolveIfNoAwakeSignalsTimer!: Timer;
	checkToActivateSleepEnderTimer!: Timer;

	override OnStart(recoveryInfo: SessionCoreData|n) {
		/*this.phaseSleep_soundPlayer.sound = GetSounds_WithUserTag(this.c.phaseSleep_soundTag).Random();
		this.phaseSleep_lightPlayer.light = GetLights_WithUserTag(this.c.phaseSleep_lightTag).Random();
		this.phaseSolving_soundPlayer.sound = GetSounds_WithUserTag(this.c.phaseSolving_soundTag).Random();
		this.phaseSolving_lightPlayer.light = GetLights_WithUserTag(this.c.phaseSolving_soundTag).Random();*/

		this.sleepUntilAlarmTimer = new Timer(1_000_000_000, ()=>this.SetPhase(Phase.dreaming), 1).SetContext(this.s.timerContext);
		this.SetSleepUntilAlarmTimerDuration(recoveryInfo?.journey_alarmDelay ?? this.GetNextSleepUntilAlarmDuration());

		// the solve-wait duration depends on configuration of whatever caused the solve-wait to happen, so just set it to a super-high initial-value for now
		this.requiredSolveProgressTimer = new Timer(1_000_000_000, ()=>this.NotifySolveFailed(), 1).SetContext(this.s.timerContext);

		this.giveUpOnSolveIfNoAwakeSignalsTimer = new Timer(1_000_000_000, ()=>this.GiveUpOnSolving(), 1).SetContext(this.s.timerContext);
		this.checkToActivateSleepEnderTimer = new Timer(10_000, ()=>this.CheckToActivateSleepEnder()).SetContext(this.s.timerContext).Start();

		// wait a moment before setting phase to initial-delay, so comps with both OnStart and OnStartPhase, have OnStart run first
		WaitXThenRun(0, ()=>{
			this.SetPhase(Phase.initialDelay);
		});
	}
	override OnStop() {
		// timer stops not really necessary (since timer-context stops all), but good practice
		this.giveUpOnSolveIfNoAwakeSignalsTimer.Stop();
		this.checkToActivateSleepEnderTimer.Stop();
		this.SetPhase(Phase.notStarted);
	}

	/*StartOrUpdateRequiredSolveProgressTimer(maxTimeForSolveProgressInMS: number) {
		const initialDelayOverride_ifWasActive = CalcInitialDelayOverrideAfterDurationChange_IfTimerActive(this.requiredSolveProgressTimer, maxTimeForSolveProgressInMS);
		this.requiredSolveProgressTimer.intervalInMS = maxTimeForSolveProgressInMS;
		console.log("InitDelayOverride:", initialDelayOverride_ifWasActive);
		this.requiredSolveProgressTimer.Start(initialDelayOverride_ifWasActive);
	}*/
	GetSolveTimeCurrentlyRemaining() {
		return this.requiredSolveProgressTimer.Enabled && this.requiredSolveProgressTimer.nextTickTime ? this.requiredSolveProgressTimer.nextTickTime - Date.now() : null;
	}
	BumpUpSolveTimeRemainingToAtLeast(minSolveTimeInMS: number) {
		const solveTimeCurrentlyRemaining = this.GetSolveTimeCurrentlyRemaining();
		if (solveTimeCurrentlyRemaining == null || solveTimeCurrentlyRemaining < minSolveTimeInMS) {
			/*this.requiredSolveProgressTimer.intervalInMS = minSolveTimeInMS;
			this.requiredSolveProgressTimer.Start();*/
			this.requiredSolveProgressTimer.Start(minSolveTimeInMS);
		}
	}
	DoesPhaseSupportSolveProgress() {
		//return this.PhaseIs(Phase.dreaming, Phase.solving);
		// "solving" phase is more multi-purpose/generic nowadays, so only block pre-initial-delay
		return !this.PhaseIs(Phase.notStarted, Phase.initialDelay);
	}
	DoesStateAllowSolveCompletion() {
		return !this.InSleepEnderActiveZone();
	}
	NotifySolveProgress(requiredProgressIntervalInSecs: number, forcePhaseChange = false, startInfo?: {phaseAfterSolveFail_override: Phase|n}) {
		// whether or not solve-progress is "actually possible" in the current situation/phase, solve-progress attempts always signal awakeness, so do this part always
		if (this.c.signalAwakeness_solveProgress) this.SignalAwakeness();

		// if solve-progress is not "actually possible" in the current situation/phase, skip rest of function (no need for warning though; solve-progress "attempts" in other phases are normal/not-harmful)
		if (!this.DoesPhaseSupportSolveProgress() && !forcePhaseChange) return;

		if (this.phase != Phase.solving) {
			this.SetPhase(Phase.solving, new PhaseChange_ExtraMessage({toPhaseSolving_requestedPhaseOnSolveFail: startInfo?.phaseAfterSolveFail_override}));
		}

		this.BumpUpSolveTimeRemainingToAtLeast(requiredProgressIntervalInSecs * 1000);
	}
	NotifySolveCompleted(signalsAwakeness = true, eventTextOverride?: string) {
		// whether or not solve-completion is "actually possible" in the current situation/phase, solve-completion attempts always signal awakeness, so do this part always
		if (signalsAwakeness) this.SignalAwakeness();

		// if solve progress/completion is not "actually possible" in the current situation/phase, skip rest of function (no need for warning though; solve progress/completion "attempts" in other phases are normal/not-harmful)
		if (!this.DoesPhaseSupportSolveProgress()) return false;
		if (!this.DoesStateAllowSolveCompletion()) return false;

		const wasAlreadySolving = this.phase == Phase.solving;
		this.SetPhase(Phase.solving);
		if (!wasAlreadySolving) this.NotifySolveProgress(10); // value doesn't matter since we're completing right away

		/*const validEntities = this.GetEntitiesMatchingAnyTag(this.c.entityTags_success);
		const successEntityText = validEntities.length ? validEntities.Random().name.split("[")[0].trim() : "Remember lucid";
		this.NarrateText(successEntityText);*/
		// speak the success-message (if set)
		if (this.c.solveSuccess_message?.trim().length != 0) {
			this.NarrateText(this.c.solveSuccess_message);
		}

		// play the success-sound (if set)
		if (this.c.solveSuccess_soundPath.length > 0) {
			const randomSoundMatching = GetAudioFilesWithSubpath(this.c.solveSuccess_soundPath).Random();
			if (randomSoundMatching) {
				PlaySound({entry: randomSoundMatching}, {volume: this.c.solveSuccess_soundVolume});
			}
		}

		RunInAction("AlarmsComp.NotifySolveCompleted", ()=>{
			this.s.AsLocal!.AddEvent({type: "Alarms.SolveSuccess"}); // needed atm for displaying info like solve-delay in journey ui (event needs rename though)
		});
		this.Log(eventTextOverride ?? "Solve was completed; entering phase drifting.", LogType.Event_Large);
		this.SetPhase(Phase.drifting);
		return true;
	}
	NotifySolveFailed() {
		this.s.AsLocal!.AddEvent({type: "Alarms.SolveFail"}); // needed atm for displaying info like solve-delay in journey ui (event needs rename though)
		const targetPhase = this.phase_changedWithExtraMessage?.toPhaseSolving_requestedPhaseOnSolveFail ?? this.c.phaseAfterSolveFail;
		this.Log(`Failed to complete solve; entering phase ${Phase[targetPhase]}.`, LogType.Event_Large);
		this.SetPhase(targetPhase);
	}
	GiveUpOnSolving() {
		this.s.AsLocal!.AddEvent({type: "Alarms.GiveUpOnSolving"});
		this.Log("Giving up on solve (due to no user interaction for X minutes), so entering phase drifting.", LogType.Event_Large);
		this.SetPhase(Phase.drifting, new PhaseChange_ExtraMessage({
			toPhaseDrifting_requestedDurationReduction: (this.s.coreData.alarms_tillAlarmEnd_endAlarmAfterXMinutesOfNoAwakeSignal ?? 0) * minuteInMS,
		}));
	}

	/** The alarm-delay candidates and tickets are in minutes, but the return-value of this func is in milliseconds. */
	GetNextSleepUntilAlarmDuration() {
		const alarmDelayCandidates = GetAlarmDelayCandidates(this.c);
		try {
			const alarmDelayTickets = GetNextAlarmDelayTickets(alarmDelayCandidates, this.c.alarmDelay_poolRates, this.c.alarmDelay_optionCatchUpRate);
			const alarmDelaySelected = alarmDelayTickets.Random();
			this.Log(`Alarm-delay tickets: ${alarmDelayTickets.join(", ")} @selected:${alarmDelaySelected}`, LogType.Event_Large);
			return alarmDelaySelected * minuteInMS;
		} catch (ex) {
			console.warn(`Failed to calculate alarm-duration based on gradual-equalizing system; returning simple-random value. Error:${ex}`);
			return alarmDelayCandidates.Random() * minuteInMS;
		}
	}
	SetSleepUntilAlarmTimerDuration(duration: number) {
		if (this.s.coreData.launchType == "day") duration = weekInMS;

		this.sleepUntilAlarmTimer.intervalInMS = duration;
		RunInAction("AlarmsComp.SetSleepUntilAlarmTimerDuration", ()=>this.s.coreData.journey_alarmDelay = duration);
	}
	StartSleepUntilAlarmTimer(requestedDurationReductionInMS: number|n) {
		if (!this.c.alarmDelay_lockPerSession) {
			this.SetSleepUntilAlarmTimerDuration(this.GetNextSleepUntilAlarmDuration());
		}
		if (requestedDurationReductionInMS) {
			// we apply the reduction directly; this way, func above does not record this temp-reduced duration in coreData (eg. for session-restore)
			this.sleepUntilAlarmTimer.intervalInMS = (this.sleepUntilAlarmTimer.intervalInMS - requestedDurationReductionInMS).KeepAtLeast(0);
		}
		this.sleepUntilAlarmTimer.Start();
	}

	readonly phase: Phase = Phase.notStarted;
	phase_changedAt = Date.now();
	phase_changedWithExtraMessage: PhaseChange_ExtraMessage|n;
	//GetPhase() { return this.phase; }
	PhaseIs(...oneOfVals: Phase[]) { return oneOfVals.includes(this.phase); }
	PhaseIsDriftLike() { return this.phase.IsOneOf(Phase.drifting, Phase.tech); }
	SetPhase(newPhase: Phase, extraMessage = new PhaseChange_ExtraMessage()) {
		if (this.phase == newPhase) {
			this.Log(`Phase is already ${Phase[newPhase]}; ignoring request to set phase to same value.`, LogType.Event_Small);
			return;
		}
		const oldPhase = this.phase;
		this.BroadcastLeavePhase(oldPhase, newPhase, extraMessage);

		this.Log(`Entering phase: ${Phase[newPhase]} (from: ${Phase[oldPhase]})`, LogType.Event_Large);
		this["phase" as any] = newPhase; // as any; this is the one place where overriding is allowed
		this.phase_changedAt = Date.now();
		this.phase_changedWithExtraMessage = extraMessage;

		this.s.AsLocal!.AddEvent({type: `Alarms.PhaseSet`, phase: newPhase});
		this.BroadcastStartPhase(oldPhase, newPhase, extraMessage);

		// phase-entry itself is an input to the trigger-set system (for the "PhaseEnter" trigger-type)
		if (this.s.phaseEnterListeners) {
			this.s.phaseEnterListeners[0](newPhase);
		}
	}
	BroadcastLeavePhase(oldPhase: Phase, newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {
		this.s.Broadcast({}, a=>a.OnLeavePhase, newPhase, oldPhase, extraMessage);
		if (oldPhase == Phase.tech) this.s.Broadcast({}, a=>a.OnLeavePhase_Tech, newPhase, extraMessage);
		else if (oldPhase == Phase.drifting) this.s.Broadcast({}, a=>a.OnLeavePhase_Drifting, newPhase, extraMessage);
		else if (oldPhase == Phase.dreaming) this.s.Broadcast({}, a=>a.OnLeavePhase_Dreaming, newPhase, extraMessage);
		else if (oldPhase == Phase.solving) this.s.Broadcast({}, a=>a.OnLeavePhase_Solving, newPhase, extraMessage);
		else if (oldPhase == Phase.delving) this.s.Broadcast({}, a=>a.OnLeavePhase_Delving, newPhase, extraMessage);
	}
	BroadcastStartPhase(oldPhase: Phase, newPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {
		this.s.Broadcast({}, a=>a.OnStartPhase, oldPhase, newPhase, extraMessage);
		if (this.phase == Phase.tech) this.s.Broadcast({}, a=>a.OnStartPhase_Tech, oldPhase, extraMessage);
		else if (this.phase == Phase.drifting) this.s.Broadcast({}, a=>a.OnStartPhase_Drifting, oldPhase, extraMessage);
		else if (this.phase == Phase.dreaming) this.s.Broadcast({}, a=>a.OnStartPhase_Dreaming, oldPhase, extraMessage);
		else if (this.phase == Phase.solving) this.s.Broadcast({}, a=>a.OnStartPhase_Solving, oldPhase, extraMessage);
		else if (this.phase == Phase.delving) this.s.Broadcast({}, a=>a.OnStartPhase_Delving, oldPhase, extraMessage);
	}
	/** When a dreaming-period starts, this becomes true; when SignalAwakeness() next gets called, the wake-effects are applied, then this flag resets to false. */
	waitingForAPostDreamingWakeEvent = false;
	SignalAwakeness() {
		this.UpdateSolveGiveUpTimer(false, true, false);

		// this effect-group only happens once per sleep-wait-end
		if (this.waitingForAPostDreamingWakeEvent) {
			this.waitingForAPostDreamingWakeEvent = false;

			this.s.AsLocal!.AddEvent({type: "General.SleepCycleEnd"});

			const {journalEntryForLiveSession} = Journey_GetSessionPeriodsToShow();
			if (journalEntryForLiveSession) {
				const segments_new = Clone(journalEntryForLiveSession.segments) as JournalSegment[];
				// if old last-segment is already a wake-period, create a dream-segment in-between that wake-segment and the new wake-segment we're about to create
				if (segments_new.LastOrX()?.wakeTime != null) {
					const anchorEntities = GetEntities_WithUserTag("anchor"); // DreamPeriodEntryUI.render() keeps this up-to-date
					segments_new.push(new JournalSegment({
						//anchorEntity: anchorEntities.Random()?.id,
					}));
				}
				segments_new.push(new JournalSegment({wakeTime: Date.now()}));
				new UpdateJournalEntry({id: journalEntryForLiveSession.id, updates: {segments: segments_new}}).RunOnServer();
				this.Log(`Marked awakeness in journal-entry for session.`, LogType.Event_Large);
			}
		}
	}

	override OnStartPhase(oldPhase: Phase, newPhase: Phase): void {
		const inAlarmOrSolving = phase=>phase.IsOneOf(Phase.dreaming, Phase.solving);

		// if entering alarm/solving union
		if (inAlarmOrSolving(newPhase) && !inAlarmOrSolving(oldPhase)) {
			this.UpdateSolveGiveUpTimer(true, false, false);
		}

		// if leaving alarm/solving union
		if (!inAlarmOrSolving(newPhase) && inAlarmOrSolving(oldPhase)) {
			RunInAction("AlarmsComp.On", ()=>{
				this.s.coreData.alarms_tillAlarmEnd_blockLightEffects = false;
				this.s.coreData.alarms_tillAlarmEnd_blockMicroSnooze = false;
				this.ClearSolveGiveUpTimerFeature();
			});
		}
	}
	ClearSolveGiveUpTimerFeature() {
		RunInAction("ClearSolveGiveUpTimerFeature", ()=>this.s.coreData.alarms_tillAlarmEnd_endAlarmAfterXMinutesOfNoAwakeSignal = 0);
		this.UpdateSolveGiveUpTimer(false, false, false);
	}
	UpdateSolveGiveUpTimer(justEnteredApplicablePhases: boolean, justSignaledAwakeness: boolean, justModifiedRequest: boolean) {
		const solveGiveUpRequestedAfterX = this.s.coreData.alarms_tillAlarmEnd_endAlarmAfterXMinutesOfNoAwakeSignal ?? 0;
		const inApplicablePhase = this.phase.IsOneOf(Phase.dreaming, Phase.solving);
		if (solveGiveUpRequestedAfterX <= 0 || !inApplicablePhase) {
			this.giveUpOnSolveIfNoAwakeSignalsTimer.Stop();
			return;
		}

		if (justEnteredApplicablePhases || justSignaledAwakeness || (justModifiedRequest && solveGiveUpRequestedAfterX > 0)) {
			this.giveUpOnSolveIfNoAwakeSignalsTimer.Start(solveGiveUpRequestedAfterX * minuteInMS);
		}
	}

	override OnStartPhase_Drifting(oldPhase: Phase, extraMessage: PhaseChange_ExtraMessage) {
		//AssertWarn(oldPhase.IsOneOf(Phase.dreaming, Phase.solving, Phase.drifting));
		//this.s.AsLocal!.AddEvent({type: "Journey.ResleepStart"});

		this.phaseSleep_soundPlayer.FindSound(this.c.phaseSleep_soundTag).Play();
		this.phaseSleep_lightPlayer.FindLight(this.c.phaseSleep_lightTag).Play();
		//ResetLight_Kasa();

		if (this.c.drift_alarmAfterDelay_enabled) {
			this.StartSleepUntilAlarmTimer(extraMessage.toPhaseDrifting_requestedDurationReduction);
		}
	}
	override OnLeavePhase_Drifting(newPhase: Phase) {
		this.sleepUntilAlarmTimer.Stop();
		this.phaseSleep_soundPlayer.Stop();
		this.phaseSleep_lightPlayer.Stop();

		if (newPhase != Phase.notStarted) {
			//this.s.AsLocal!.AddEvent({type: "Journey.ResleepEnd"});

			const modeSwitcher = this.s.Comp(ModeSwitcherComp);
			const targetComp = modeSwitcher.c.onAlarmWaitEnd_setActiveMode;
			//const cycleEntryMatches = modeSwitcher.c.cycleEntries.Any(a=>a.compID == targetComp && !(a.nightOnly && this.s.daytimeMode));
			if (targetComp && modeSwitcher.behaviorEnabled) {
				modeSwitcher.SetActiveComp_ByCompID(targetComp, false);
			}
		}
	}

	// NOTE: These functions assume each sequence has a unique name. (ui layer should probably just enforce this later)
	GetCurrentAlarmSequence(): AlarmSequence|n {
		return this.c.phaseAlarm_sequences.find(a=>a.name == this.s.coreData.alarms_currentSequence);
	}
	SetAlarmCurrentSequence(newSequence: AlarmSequence|n) {
		const oldSequenceName = this.s.coreData.alarms_currentSequence;
		const newSequenceName = newSequence?.name;
		if (newSequenceName == oldSequenceName) return;

		RunInAction("AlarmsComp.OnStartPhase_Alarm", ()=>this.s.coreData.alarms_currentSequence = newSequenceName);

		// disable old sequence's comps
		for (const alarmComp of this.s.components.filter(a=>a instanceof AlarmComp && a.behaviorEnabled) as AlarmComp<any>[]) {
			if (alarmComp.sequence.name == oldSequenceName) {
				alarmComp.OnSequenceDisabled();
			}
		}
	}

	InSleepEnderActiveZone() {
		return this.s.coreData.alarms_sleepEnderActivatableFrom != null && Date.now() >= this.s.coreData.alarms_sleepEnderActivatableFrom;
	}
	CheckToActivateSleepEnder() {
		const sleepEnderSequence = this.c.phaseAlarm_sequences.find(a=>a.name == "SleepEnder");
		const useSleepEnder = this.InSleepEnderActiveZone() && sleepEnderSequence != null;
		if (useSleepEnder && this.s.coreData.alarms_currentSequence != sleepEnderSequence.name) {
			this.SetAlarmCurrentSequence(sleepEnderSequence);
			// phase-set needs to happen, even if already in alarm phase (since this triggers the needed logic in OnStartPhase_Alarm below)
			this.SetPhase(Phase.dreaming);
		}
	}

	override OnStartPhase_Dreaming(oldPhase: Phase) {
		//AssertWarn(oldPhase.IsOneOf(Phase.initialDelay, Phase.solving, Phase.drifting));

		const sleepEnderSequence = this.c.phaseAlarm_sequences.find(a=>a.name == "SleepEnder");
		const useSleepEnder = this.InSleepEnderActiveZone() && sleepEnderSequence != null;
		if (this.GetCurrentAlarmSequence() == null || !this.c.phaseAlarm_sequences_lockPerSession || (useSleepEnder && this.s.coreData.alarms_currentSequence != sleepEnderSequence.name)) {
			const newSequence = useSleepEnder ? sleepEnderSequence : this.c.phaseAlarm_sequences.filter(a=>a.enabled).Random();
			this.SetAlarmCurrentSequence(newSequence);
		}

		//this.s.AsLocal.AddEvent({type: "Journey.PhaseStart_EntryWaitBright"});
		const event = {type: "Alarms.SequenceStart"} as Partial<SessionEvent>;
		if (this.GetCurrentAlarmSequence()) event.alarmSequence = this.GetCurrentAlarmSequence()!.name;
		this.s.AsLocal!.AddEvent(event);

		// start new sequence's comps (should happen whether target-sequence has changed or not)
		for (const alarmComp of this.s.components.filter(a=>a instanceof AlarmComp && a.behaviorEnabled) as AlarmComp<any>[]) {
			if (alarmComp.sequence == this.GetCurrentAlarmSequence()) {
				alarmComp.OnSequenceStarted();
			}
		}

		this.waitingForAPostDreamingWakeEvent = true;
	}
	override OnLeavePhase_Dreaming(newPhase: Phase) {}

	override OnStartPhase_Solving(oldPhase: Phase) {
		//AssertWarn(oldPhase.IsOneOf(Phase.dreaming, Phase.solving));

		// needed atm for displaying info like solve-delay in journey ui (event needs rename though)
		// note: this is arguably a legacy event (since PhaseSet:solving is already recorded); probably remove eventually
		this.s.AsLocal!.AddEvent({type: "Alarms.SolveStart"});

		this.phaseSolving_soundPlayer.FindSound(this.c.phaseSolving_soundTag).Play(this.c.volumeMultiplier);
		this.phaseSolving_lightPlayer.FindLight(this.c.phaseSolving_soundTag).Play(this.c.volumeMultiplier);
	}
	override OnLeavePhase_Solving(newPhase: Phase) {
		this.requiredSolveProgressTimer.Stop();
		this.phaseSolving_soundPlayer.Stop();
		this.phaseSolving_lightPlayer.Stop();
	}
}